import React, { useMemo, useState } from 'react';
import AnalyticsSettingsPanel, { useAnalyticsSettings, SettingsButton } from '../common/AnalyticsSettings';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';

const AVAILABLE_METRICS = [
  { id: 'totalRecords', label: 'Total Records', icon: '📋', defaultVisible: true },
  { id: 'totalQty', label: 'Total Quantity', icon: '📦', defaultVisible: true },
  { id: 'totalBeforeTariff', label: 'Before Tariff', icon: '💵', defaultVisible: true },
  { id: 'totalAfterTariff', label: 'After Tariff', icon: '💰', defaultVisible: true },
];

const AVAILABLE_CHARTS = [
  { id: 'byDescription', label: 'By Description', icon: '📝', defaultVisible: true, defaultType: 'bar' },
  { id: 'byMaterial', label: 'By Material', icon: '🔧', defaultVisible: true, defaultType: 'bar' },
  { id: 'byCOO', label: 'By Country of Origin', icon: '🌍', defaultVisible: true, defaultType: 'bar' },
  { id: 'tariffBreakdown', label: 'Tariff Breakdown', icon: '📊', defaultVisible: true, defaultType: 'bar' },
];

/**
 * PurchasingAnalyticsDashboard - Analytics Dashboard for Purchasing List
 * Displays purchase orders, cost analysis, and tariff impact
 */
const PurchasingAnalyticsDashboard = ({ data, onClose }) => {
  const [selectedView, setSelectedView] = useState('overview');
  const [showSettings, setShowSettings] = useState(false);

  // Real-time presence tracking (shows who's viewing this page)
  const { viewers, isConnected, connectionState } = usePresence('purchasingAnalytics', 'global', true);
  
  const {
    settings,
    updateSetting,
    toggleMetric,
    toggleChart,
    setChartType,
    resetToDefaults,
    isMetricVisible,
    isChartVisible,
  } = useAnalyticsSettings('purchasing_log', AVAILABLE_METRICS, AVAILABLE_CHARTS);

  const analytics = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        totalRecords: 0,
        totalQty: 0,
        totalBeforeTariff: 0,
        totalAfterTariff: 0,
        tariffImpact: 0,
        uniqueDescriptions: 0,
        uniqueMaterials: 0,
        uniqueCOO: 0,
        byDescription: [],
        byMaterial: [],
        byCOO: [],
        priceDistribution: [],
        tariffBreakdown: [],
      };
    }

    const totalRecords = data.length;
    const totalQty = data.reduce((sum, d) => sum + (parseFloat(d.qty) || parseFloat(d.mto_quan) || 0), 0);
    const totalBeforeTariff = data.reduce((sum, d) => sum + (parseFloat(d.total_price_before_tariff) || 0), 0);
    const totalAfterTariff = data.reduce((sum, d) => sum + (parseFloat(d.total_price_after_tariff) || 0), 0);
    const tariffImpact = totalAfterTariff - totalBeforeTariff;

    const uniqueDescriptions = new Set(data.map(d => d.mto_desc).filter(Boolean)).size;
    const uniqueMaterials = new Set(data.map(d => d.mto_mate).filter(Boolean)).size;
    const uniqueCOO = new Set(data.map(d => d.coo).filter(Boolean)).size;

    // By Description
    const descMap = {};
    data.forEach(d => {
      const desc = d.mto_desc || 'Unspecified';
      if (!descMap[desc]) {
        descMap[desc] = { name: desc, count: 0, qty: 0, totalBefore: 0, totalAfter: 0 };
      }
      descMap[desc].count++;
      descMap[desc].qty += parseFloat(d.qty) || parseFloat(d.mto_quan) || 0;
      descMap[desc].totalBefore += parseFloat(d.total_price_before_tariff) || 0;
      descMap[desc].totalAfter += parseFloat(d.total_price_after_tariff) || 0;
    });
    const byDescription = Object.values(descMap).sort((a, b) => b.totalAfter - a.totalAfter).slice(0, 15);

    // By Material
    const matMap = {};
    data.forEach(d => {
      const mat = d.mto_mate || 'Unspecified';
      if (!matMap[mat]) {
        matMap[mat] = { name: mat, count: 0, qty: 0, totalBefore: 0, totalAfter: 0 };
      }
      matMap[mat].count++;
      matMap[mat].qty += parseFloat(d.qty) || parseFloat(d.mto_quan) || 0;
      matMap[mat].totalBefore += parseFloat(d.total_price_before_tariff) || 0;
      matMap[mat].totalAfter += parseFloat(d.total_price_after_tariff) || 0;
    });
    const byMaterial = Object.values(matMap).sort((a, b) => b.totalAfter - a.totalAfter).slice(0, 15);

    // By COO
    const cooMap = {};
    data.forEach(d => {
      const coo = d.coo || 'Unspecified';
      if (!cooMap[coo]) {
        cooMap[coo] = { name: coo, count: 0, totalBefore: 0, totalAfter: 0, tariffImpact: 0 };
      }
      cooMap[coo].count++;
      cooMap[coo].totalBefore += parseFloat(d.total_price_before_tariff) || 0;
      cooMap[coo].totalAfter += parseFloat(d.total_price_after_tariff) || 0;
      cooMap[coo].tariffImpact = cooMap[coo].totalAfter - cooMap[coo].totalBefore;
    });
    const byCOO = Object.values(cooMap).sort((a, b) => b.tariffImpact - a.tariffImpact).slice(0, 15);

    // Tariff breakdown
    const adTotal = data.reduce((sum, d) => sum + (parseFloat(d.ad_percent) || 0), 0);
    const cvTotal = data.reduce((sum, d) => sum + (parseFloat(d.cv_percent) || 0), 0);
    const sec232Total = data.reduce((sum, d) => sum + (parseFloat(d.section_232_percent) || 0), 0);
    const sec301Total = data.reduce((sum, d) => sum + (parseFloat(d.section_301_percent) || 0), 0);
    const recipTotal = data.reduce((sum, d) => sum + (parseFloat(d.reciprocal_percent) || 0), 0);

    const tariffBreakdown = [
      { name: 'AD', value: adTotal, avg: totalRecords > 0 ? adTotal / totalRecords : 0 },
      { name: 'CV', value: cvTotal, avg: totalRecords > 0 ? cvTotal / totalRecords : 0 },
      { name: 'Sec 232', value: sec232Total, avg: totalRecords > 0 ? sec232Total / totalRecords : 0 },
      { name: 'Sec 301', value: sec301Total, avg: totalRecords > 0 ? sec301Total / totalRecords : 0 },
      { name: 'Reciprocal', value: recipTotal, avg: totalRecords > 0 ? recipTotal / totalRecords : 0 },
    ];

    return {
      totalRecords,
      totalQty,
      totalBeforeTariff,
      totalAfterTariff,
      tariffImpact,
      tariffPercentage: totalBeforeTariff > 0 ? (tariffImpact / totalBeforeTariff) * 100 : 0,
      uniqueDescriptions,
      uniqueMaterials,
      uniqueCOO,
      byDescription,
      byMaterial,
      byCOO,
      tariffBreakdown,
    };
  }, [data]);

  const colors = {
    primary: '#6366f1',
    secondary: '#8b5cf6',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#3b82f6',
    dark: '#1e293b',
    chartColors: ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6'],
  };

  const formatCurrency = (num) => {
    if (num >= 1000000) return '$' + (num / 1000000).toFixed(2) + 'M';
    if (num >= 1000) return '$' + (num / 1000).toFixed(1) + 'K';
    return '$' + num.toFixed(2);
  };

  const formatNumber = (num, decimals = 0) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toFixed(decimals);
  };

  const MetricCard = ({ title, value, subtitle, icon, color }) => (
    <div style={{
      backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', borderLeft: `4px solid ${color}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>{title}</span>
        <span style={{ fontSize: '1.5rem' }}>{icon}</span>
      </div>
      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: colors.dark, margin: '0.5rem 0' }}>{value}</div>
      {subtitle && <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{subtitle}</span>}
    </div>
  );

  const BarChart = ({ data, valueKey, labelKey, title, maxBars = 10, formatValue }) => {
    const chartData = data.slice(0, maxBars);
    const maxValue = Math.max(...chartData.map(d => d[valueKey] || 0), 1);

    return (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <h4 style={{ margin: '0 0 1rem', color: colors.dark, fontSize: '1rem', fontWeight: '600' }}>{title}</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {chartData.map((item, idx) => {
            const value = item[valueKey] || 0;
            const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ width: '120px', fontSize: '0.75rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={item[labelKey]}>
                  {item[labelKey]}
                </div>
                <div style={{ flex: 1, height: '24px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${percentage}%`, height: '100%',
                    backgroundColor: colors.chartColors[idx % colors.chartColors.length],
                    borderRadius: '4px', transition: 'width 0.5s ease',
                  }} />
                </div>
                <div style={{ width: '80px', fontSize: '0.75rem', color: '#64748b', textAlign: 'right' }}>
                  {formatValue ? formatValue(value) : formatNumber(value)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'costs', label: 'Cost Analysis', icon: '💰' },
    { id: 'tariffs', label: 'Tariff Impact', icon: '📈' },
  ];

  const renderOverview = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <MetricCard title="Total Items" value={analytics.totalRecords} subtitle={`${analytics.uniqueDescriptions} unique items`} icon="📦" color={colors.primary} />
        <MetricCard title="Total Quantity" value={formatNumber(analytics.totalQty)} subtitle="Units ordered" icon="📋" color={colors.info} />
        <MetricCard title="Before Tariff" value={formatCurrency(analytics.totalBeforeTariff)} subtitle="Base cost" icon="💵" color={colors.success} />
        <MetricCard title="After Tariff" value={formatCurrency(analytics.totalAfterTariff)} subtitle="Final cost" icon="💰" color={colors.warning} />
      </div>
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', marginBottom: '1.5rem' }}>
        <h4 style={{ margin: '0 0 1rem', color: colors.dark, fontSize: '1rem', fontWeight: '600' }}>Tariff Impact Summary</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ color: '#64748b' }}>Total Tariff Cost</span>
              <span style={{ fontWeight: '700', color: colors.danger }}>{formatCurrency(analytics.tariffImpact)}</span>
            </div>
            <div style={{ height: '12px', backgroundColor: '#e2e8f0', borderRadius: '6px', overflow: 'hidden' }}>
              <div style={{ width: `${Math.min(analytics.tariffPercentage, 100)}%`, height: '100%', backgroundColor: colors.danger, borderRadius: '6px' }} />
            </div>
            <div style={{ textAlign: 'right', marginTop: '0.25rem', fontSize: '0.8rem', color: '#64748b' }}>
              {analytics.tariffPercentage.toFixed(1)}% increase
            </div>
          </div>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem' }}>
        <BarChart data={analytics.byDescription} valueKey="totalAfter" labelKey="name" title="Top Items by Cost" formatValue={formatCurrency} />
        <BarChart data={analytics.byMaterial} valueKey="count" labelKey="name" title="Items by Material" />
      </div>
    </>
  );

  const renderCosts = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <BarChart data={analytics.byDescription} valueKey="totalBefore" labelKey="name" title="Cost Before Tariff by Item" formatValue={formatCurrency} />
        <BarChart data={analytics.byDescription} valueKey="qty" labelKey="name" title="Quantity by Item" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem' }}>
        <BarChart data={analytics.byMaterial} valueKey="totalAfter" labelKey="name" title="Total Cost by Material" formatValue={formatCurrency} />
        <BarChart data={analytics.byMaterial} valueKey="qty" labelKey="name" title="Quantity by Material" />
      </div>
    </>
  );

  const renderTariffs = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        {analytics.tariffBreakdown.map((t, idx) => (
          <div key={idx} style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1rem', textAlign: 'center', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
            <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.5rem' }}>{t.name}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: colors.chartColors[idx] }}>{t.avg.toFixed(2)}%</div>
            <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>avg rate</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem' }}>
        <BarChart data={analytics.byCOO} valueKey="tariffImpact" labelKey="name" title="Tariff Impact by Country of Origin" formatValue={formatCurrency} />
        <BarChart data={analytics.byCOO} valueKey="count" labelKey="name" title="Items by Country of Origin" />
      </div>
    </>
  );

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)',
      zIndex: 10000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem',
    }}>
      <div style={{
        backgroundColor: '#f1f5f9', borderRadius: '16px', width: '100%', maxWidth: '1400px', maxHeight: '95vh',
        overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      }}>
        <div style={{
          padding: '1.25rem 1.5rem', background: `linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700' }}>🛒 Purchasing Analytics</h2>
            <p style={{ margin: '0.25rem 0 0', opacity: 0.9, fontSize: '0.85rem' }}>Cost analysis, tariff impact, and purchasing breakdown</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
            <button onClick={onClose} style={{
              padding: '0.5rem 1rem', backgroundColor: 'rgba(255, 255, 255, 0.2)', color: 'white',
              border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem',
            }}>✕ Close</button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', padding: '1rem 1.5rem 0', backgroundColor: 'white', borderBottom: '1px solid #e2e8f0' }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setSelectedView(tab.id)} style={{
              padding: '0.75rem 1.25rem', backgroundColor: selectedView === tab.id ? colors.secondary : 'transparent',
              color: selectedView === tab.id ? 'white' : '#64748b', border: 'none', borderRadius: '8px 8px 0 0',
              cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.5rem',
            }}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem' }}>
          {analytics.totalRecords === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748b' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📭</div>
              <h3 style={{ margin: 0, color: colors.dark }}>No Data Available</h3>
              <p style={{ margin: '0.5rem 0 0' }}>Add purchasing data to see analytics</p>
            </div>
          ) : (
            <>
              {selectedView === 'overview' && renderOverview()}
              {selectedView === 'costs' && renderCosts()}
              {selectedView === 'tariffs' && renderTariffs()}
            </>
          )}
        </div>

        <div style={{
          padding: '0.75rem 1.5rem', backgroundColor: 'white', borderTop: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', color: '#94a3b8',
        }}>
          <span>{analytics.totalRecords} items | {formatNumber(analytics.totalQty)} units</span>
          <span>Total: {formatCurrency(analytics.totalAfterTariff)} (incl. {formatCurrency(analytics.tariffImpact)} tariffs)</span>
        </div>
      </div>
    </div>
  );
};

export default PurchasingAnalyticsDashboard;

